import React, { useMemo } from 'react';
import { Divider, Result } from '@fx-ui/fine-design';
import { forEach, isEmpty, map } from '@libs/shared';
import { authModel } from '../../../../models';
import { platformIntl } from '../../../../constants';
import emptyImg from '../../../../assets/empty/message-empty.png';
import { MultiBaseCreateCompanyAndTeamIcon, MultiBaseJoinCompanyAndTeamIcon } from '@fx-ui/icon';
import { UnreadMessageStatus } from '../../components';

export interface CorpUnreadMessage {
    tenant_id: string;
    is_owner: boolean;
    corp_name: string;
    has_unread: boolean;
}

interface ExternalMessageProps {
    externalMessage: CorpUnreadMessage[];
}

/**
 * 消息中心-外部消息
 * 展示其他企业的消息
 * @param props ExternalMessageProps
 */
const ExternalMessage: React.FC<ExternalMessageProps> = (props) => {
    const { externalMessage } = props;
    const { createdCorps, joinedCorps, corpList } = useMemo(() => {
        const createdCorps: CorpUnreadMessage[] = [];
        const joinedCorps: CorpUnreadMessage[] = [];
        forEach(externalMessage, (item) => {
            if (item.is_owner) {
                createdCorps.push(item);
            } else {
                joinedCorps.push(item);
            }
        });

        const corpList = [
            {
                title: platformIntl.get('corp.my.create.team'),
                icon: <MultiBaseCreateCompanyAndTeamIcon className="team-icon" />,
                corps: createdCorps
            },
            {
                title: platformIntl.get('corp.my.join.team'),
                icon: <MultiBaseJoinCompanyAndTeamIcon className="team-icon" />,
                corps: joinedCorps
            }
        ];
        return { createdCorps, joinedCorps, corpList };
    }, [externalMessage]);

    const renderOtherCorpMessageList = () => {
        if (isEmpty(createdCorps) && isEmpty(joinedCorps)) {
            return (
                <Result
                    className="message-empty"
                    description={platformIntl.get('message.empty')}
                    image={emptyImg}
                    imageStyle={{ height: 140 }}
                />
            );
        }

        return (
            <div className="other-corp-message-list">
                {map(corpList, (item) => {
                    return (
                        <div className="message-corp-group" key={item.title}>
                            <div className="group-container">
                                <div className="group-icon">{item.icon}</div>
                                <div className="group-content">
                                    <div className="group-header">{item.title}</div>
                                    {map(item.corps, (corp) => {
                                        const isCurrentCorp = authModel.tenantId === corp.tenant_id;
                                        return (
                                            <div className="corp-message" key={corp.tenant_id}>
                                                <UnreadMessageStatus
                                                    hasUnread={corp.has_unread && !isCurrentCorp}
                                                    dot
                                                    offset={[6, -2]}
                                                >
                                                    <span className="message-corp-name">
                                                        {corp.corp_name}
                                                    </span>
                                                </UnreadMessageStatus>
                                                {isCurrentCorp && (
                                                    <span className="is-current-corp">
                                                        {platformIntl.get('corp.current')}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <Divider className="corp-group-divider" />
                        </div>
                    );
                })}
                <div className="unread-tip">{platformIntl.get('message.external.tip')}</div>
            </div>
        );
    };

    return <div className="fx-external-message">{renderOtherCorpMessageList()}</div>;
};

export default ExternalMessage;
